import React, { useEffect, useCallback, useRef } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Platform, Dimensions } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useAuth } from '../hooks/useAuth';
import { SubscriptionPlan } from '../types/auth';
import AppToolbar from '../components/shared/AppToolbar';
import { reset as resetNavigation } from './NavigationService';
import {
  getAuthNavigationAction,
  getPostAuthRoute,
  getPostLogoutRoute,
} from '../utils/authNavigation';

import LandingScreen from '../screens/landing/LandingScreen';
import PublicColorGeneratorScreen from '../screens/public/PublicColorGeneratorScreen';
import PublicExploreColoursScreen from '../screens/public/PublicExploreColoursScreen';
import PublicColorAnalysisScreen from '../screens/public/PublicColorAnalysisScreen';
import PublicVirtualDrapeScreen from '../screens/public/PublicVirtualDrapeScreen';
import LoginScreen from '../screens/auth/LoginScreen';
import RegisterScreen from '../screens/auth/RegisterScreen';
import OnboardingScreen from '../screens/auth/OnboardingScreen';
import ChangePasswordScreen from '../screens/auth/ChangePasswordScreen';
import ForgotPasswordScreen from '../screens/auth/ForgotPasswordScreen';
import AuthCallbackScreen from '../screens/auth/AuthCallbackScreen';
import VerifyEmailRequiredScreen from '../screens/auth/VerifyEmailRequiredScreen';
import InviteScreen from '../screens/invite/InviteScreen';

import ProfileScreen from '../screens/profile/ProfileScreen';
import ColourAnalysisScreen from '../screens/color-analysis/ColourAnalysisScreen';
import ColourDetailScreen from '../screens/color-analysis/ColourDetailScreen';
import WearThisTodayScreen from '../screens/style-consultation/WearThisTodayScreen';
import VirtualDrapeTestScreen from '../screens/virtual-drape/VirtualDrapeTestScreen';
import ColorGeneratorScreen from '../screens/color-generator/ColorGeneratorScreen';
import PremiumPaywallScreen from '../screens/billing/PremiumPaywallScreen';
import MoodBoardScreen from '../screens/moodboard/MoodBoardScreen';
import MoodBoardDetailScreen from '../screens/moodboard/MoodBoardDetailScreen';
import SettingsScreen from '../screens/settings/SettingsScreen';
import BillingScreen from '../screens/billing/BillingScreen';
import PricingScreen from '../screens/pricing/PricingScreen';
import BillingSupportScreen from '../screens/billing/BillingSupportScreen';
import HelpCenterScreen from '../screens/help-center/HelpCenterScreen';
import AccessibilitySettingsScreen from '../screens/settings/AccessibilitySettingsScreen';
import LanguageSelectionScreen from '../screens/settings/LanguageSelectionScreen';
import ThemeSelectionScreen from '../screens/settings/ThemeSelectionScreen';
import ColorAnalysisDetailScreen from '../screens/color-analysis/ColorAnalysisDetailScreen';
import VirtualDrapeDetailScreen from '../screens/virtual-drape/VirtualDrapeDetailScreen';
import SmartWardrobeDetailScreen from '../screens/smart-wardrobe/SmartWardrobeDetailScreen';
import PaletteGeneratorDetailScreen from '../screens/color-generator/PaletteGeneratorDetailScreen';
import StyleConsultationDetailScreen from '../screens/style-consultation/StyleConsultationDetailScreen';
import MakeupMatchingDetailScreen from '../screens/makeup/MakeupMatchingDetailScreen';
import BlogListScreen from '../screens/blog/BlogListScreen';
import BlogArticleScreen from '../screens/blog/BlogArticleScreen';
import PrivacyPolicyScreen from '../screens/public/PrivacyPolicyScreen';
import TermsOfServiceScreen from '../screens/public/TermsOfServiceScreen';
import FAQScreen from '../screens/public/FAQScreen';
import AdminScreen from '../screens/admin/AdminScreen';
import AdminLoginScreen from '../screens/auth/AdminLoginScreen';
import BottomTabNavigator from './BottomTabNavigator';
import SystemAnnouncementBanner from '../components/shared/SystemAnnouncementBanner';
import AuthGuard from '../components/shared/AuthGuard';
import { RootStackParamList } from '../types/navigation';

const Stack = createNativeStackNavigator<RootStackParamList>();

const UserProfileWrapper = ({
  navigation,
}: {
  navigation: { replace: (name: string, params?: object) => void };
}) => {
  useEffect(() => {
    if (Platform.OS === 'web') {
      navigation.replace('MainTabs', { section: 'profile' });
    }
  }, [navigation]);

  if (Platform.OS !== 'web') {
    return <ProfileScreen />;
  }

  return null;
};

const SECTION_TO_TAB: Record<string, string> = {
  home: 'HomeTab',
  'mood-boards': 'MoodBoardTab',
  'smart-wardrobe': 'SmartWardrobe',
  'color-analysis': 'ColourAnalysis',
  'virtual-drape-test': 'VirtualDrapeTest',
  'color-generator': 'ColorGenerator',
  'color-library': 'ColorLibrary',
  settings: 'SettingsTab',
  profile: 'ProfileTab',
};

const TAB_TO_SECTION: Record<string, string> = {
  HomeTab: 'home',
  MoodBoardTab: 'mood-boards',
  ColourAnalysis: 'color-analysis',
  VirtualDrapeTest: 'virtual-drape-test',
  ColorGenerator: 'color-generator',
  ColorLibrary: 'color-library',
  SmartWardrobe: 'smart-wardrobe',
  SettingsTab: 'settings',
  ProfileTab: 'profile',
  ColourAnalysisMobile: 'color-analysis',
  VirtualDrapeTestMobile: 'virtual-drape-test',
  ColorGeneratorMobile: 'color-generator',
  ColorLibraryMobile: 'color-library',
  MoodBoardTabMobile: 'mood-boards',
  ProfileTabMobile: 'profile',
};

function _getInitialTabFromParams(
  params: RootStackParamList['MainTabs'] | undefined
): string {
  // Use route params as single source of truth - React Navigation linking handles URL parsing
  const section =
    params?.section && params.section !== 'undefined'
      ? params.section
      : undefined;
  if (section && SECTION_TO_TAB[section]) return SECTION_TO_TAB[section];
  if (params?.initialTab) return params.initialTab;
  return 'HomeTab';
}

const MainTabsWithToolbar = () => {
  const route = useRoute();
  const routeParams = route.params as RootStackParamList['MainTabs'];
  const navigation = useNavigation();

  // Derive active tab from route params (single source of truth)
  // React Navigation's linking configuration parses URL and sets route.params.section
  // This works for both web (desktop and mobile web) and native mobile
  const section =
    routeParams?.section && routeParams.section !== 'undefined'
      ? routeParams.section
      : routeParams?.initialTab
        ? (TAB_TO_SECTION[routeParams.initialTab] ?? 'home')
        : 'home';

  const activeTab = SECTION_TO_TAB[section] ?? 'HomeTab';

  // Ensure route params are normalized on initial load ONLY
  // This effect should only run once on mount, not on every param change
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    // Skip if we've already initialized
    if (hasInitializedRef.current) return;

    // Only proceed if we have route params
    if (!routeParams) return;

    // Only set default section if it's completely missing (first mount only)
    // Once we have a section (even if it's 'home'), don't touch it again
    if (
      !routeParams.section ||
      routeParams.section === 'undefined' ||
      routeParams.section === ''
    ) {
      if (routeParams.initialTab) {
        // Use initialTab to derive section
        const derivedSection = TAB_TO_SECTION[routeParams.initialTab] ?? 'home';
        navigation.setParams({
          section: derivedSection,
        } as RootStackParamList['MainTabs']);
      } else {
        // Default to home only if we have no section info at all
        navigation.setParams({
          section: 'home',
        } as RootStackParamList['MainTabs']);
      }
      // Mark as initialized after setting default
      hasInitializedRef.current = true;
    } else {
      // We have a valid section, mark as initialized
      hasInitializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- routeParams intentionally excluded to avoid re-running on param changes
  }, [routeParams?.section, routeParams?.initialTab, navigation]);

  // Sync URL with route params on web
  // This ensures URLs stay in sync with params, especially on mobile web
  useEffect(() => {
    // Only sync URL if we have a valid section
    if (
      !routeParams?.section ||
      routeParams.section === 'undefined' ||
      routeParams.section === ''
    ) {
      return;
    }

    // On web, ensure URL matches params
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const currentSection = routeParams.section;
      const expectedPath =
        currentSection === 'home'
          ? '/dashboard'
          : `/dashboard/${currentSection}`;
      const currentPath = window.location.pathname.replace(/\/$/, '');
      const normalizedExpected = expectedPath.replace(/\/$/, '');

      // Only update if URL doesn't match expected path
      if (currentPath !== normalizedExpected) {
        // Use replaceState to update URL without adding to history (since handleTabChange uses pushState)
        window.history.replaceState({}, '', expectedPath);
      }
    }
  }, [routeParams?.section]);

  // Handle browser back/forward buttons on web
  // React Navigation's linking should handle this, but we ensure route params sync with URL
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const onPopState = () => {
      // When browser navigation occurs, parse URL and update route params
      const pathname = window.location.pathname.replace(/\/$/, '');
      let urlSection: string;

      if (pathname === '/dashboard' || pathname === '') {
        urlSection = 'home';
      } else {
        const match = pathname.match(/\/dashboard\/([^/]+)/);
        urlSection = match && match[1] !== 'undefined' ? match[1] : 'home';
      }

      // Update route params to match URL (React Navigation linking should do this, but ensure it)
      if (routeParams?.section !== urlSection) {
        navigation.setParams({
          section: urlSection,
        } as RootStackParamList['MainTabs']);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [routeParams?.section, navigation]);

  // Document title is handled by React Navigation's documentTitle formatter in App.tsx
  // It receives route.name='MainTabs' and route.params.section
  // React Navigation automatically calls the formatter when route params change

  const handleTabChange = useCallback(
    (tab: string) => {
      const newSection = TAB_TO_SECTION[tab] || 'home';

      // Update navigation params - this is the correct way to update params without resetting state
      navigation.setParams({
        section: newSection,
      } as RootStackParamList['MainTabs']);

      // On web, manually update URL to ensure it changes (React Navigation linking may not update on mobile web)
      // Do this after setParams so React Navigation processes the params first
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Use requestAnimationFrame to ensure setParams has been processed
        requestAnimationFrame(() => {
          const newPath =
            newSection === 'home' ? '/dashboard' : `/dashboard/${newSection}`;
          // Use pushState to update URL and add to history (better for browser back button)
          window.history.pushState({}, '', newPath);
        });
      }
    },
    [navigation]
  );

  return (
    <AuthGuard>
      <>
        <SystemAnnouncementBanner />
        <BottomTabNavigator
          activeTab={activeTab}
          onTabPress={handleTabChange}
          routeParams={routeParams}
        />
      </>
    </AuthGuard>
  );
};

const Navigation = () => {
  const {
    isAuthenticated,
    subscriptionPlan,
    needsOnboarding,
    needsEmailVerification,
    isLoading: isAuthLoading,
  } = useAuth();

  const prevAuthenticatedRef = useRef<boolean | null>(null);

  // Navigate immediately when auth state changes (login/logout/sign-up).
  // Conditional stack groups remount on auth change; explicit reset avoids stale screens.
  useEffect(() => {
    const wasAuthenticated = prevAuthenticatedRef.current;
    prevAuthenticatedRef.current = isAuthenticated;

    const action = getAuthNavigationAction(
      wasAuthenticated,
      isAuthenticated,
      isAuthLoading
    );

    if (action === 'authenticated') {
      const route = getPostAuthRoute({
        needsEmailVerification: needsEmailVerification(),
        needsOnboarding: needsOnboarding(),
      });
      resetNavigation(0, [route]);
      return;
    }

    if (action === 'logout') {
      resetNavigation(0, [getPostLogoutRoute()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- needsOnboarding/needsEmailVerification are auth checks, not effect triggers
  }, [isAuthenticated, isAuthLoading]);

  const defaultScreenOptions = {
    headerShown: true,
    header: () => <AppToolbar showBackButton={true} />,
  };

  const getScreenOptionsWithTitle = (title: string) => ({
    headerShown: true,
    header: () => <AppToolbar showBackButton={true} title={title} />,
  });

  const getScreenOptionsWithTitleNoProfile = (title: string) => ({
    headerShown: true,
    header: () => (
      <AppToolbar showBackButton={true} title={title} showProfile={false} />
    ),
  });

  const getSettingsScreenOptions = (title: string) => ({
    headerShown: true,
    header: () => {
      const screenWidth = Dimensions.get('window').width;
      const isWebLayout = Platform.OS === 'web' && screenWidth >= 768;

      return (
        <AppToolbar
          showBackButton={true}
          title={title}
          showSettings={isWebLayout}
          showShare={isWebLayout}
          showProfile={isWebLayout}
        />
      );
    },
  });

  const getBlogScreenOptions = (title: string) => ({
    headerShown: true,
    header: () => (
      <AppToolbar
        showBackButton={false}
        title={title}
        showProfile={false}
        showShare={false}
        showSettings={false}
      />
    ),
  });

  const _getScreenOptionsWithTitleAndButton = (
    title: string,
    rightButton: { icon: string; onPress: () => void }
  ) => ({
    headerShown: true,
    header: () => (
      <AppToolbar
        showBackButton={true}
        title={title}
        rightButton={rightButton}
      />
    ),
  });

  const getDynamicScreenOptions = ({
    route,
    navigation,
  }: {
    route: { name: string };
    navigation: { navigate: (name: string, params?: object) => void };
  }) => {
    if (route.name === 'MoodBoardDetail') {
      return {
        headerShown: true,
        header: () => (
          <AppToolbar
            showBackButton={true}
            title="Mood Board"
            showSettings={false}
            showProfile={false}
            showShare={false}
            headerVariant="highlight"
            onBackPress={() => {
              if (Platform.OS === 'web') {
                navigation.navigate('MainTabs', { section: 'mood-boards' });
              } else {
                navigation.navigate('MainTabs', { initialTab: 'MoodBoardTab' });
              }
            }}
          />
        ),
      };
    }
    return {
      headerShown: true,
      header: () => <AppToolbar showBackButton={true} />,
    };
  };

  const tabScreenOptions = {
    headerShown: false,
  };

  const authScreenOptions = {
    headerShown: false,
  };

  const publicScreens = () => (
    <>
      <Stack.Screen
        name="BlogList"
        component={BlogListScreen}
        options={getBlogScreenOptions('Blog')}
      />
      <Stack.Screen
        name="BlogArticle"
        component={BlogArticleScreen}
        options={getBlogScreenOptions('Article')}
      />
      <Stack.Screen
        name="PrivacyPolicy"
        component={PrivacyPolicyScreen}
        options={{
          headerShown: false,
          title: 'ColourSense | Privacy Policy',
        }}
      />
      <Stack.Screen
        name="TermsOfService"
        component={TermsOfServiceScreen}
        options={{
          headerShown: false,
          title: 'ColourSense | Terms of Service',
        }}
      />
      <Stack.Screen
        name="FAQ"
        component={FAQScreen}
        options={{
          headerShown: false,
          title: 'ColourSense | FAQ',
        }}
      />
      <Stack.Screen
        name="Invite"
        component={InviteScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="ForgotPassword"
        component={ForgotPasswordScreen}
        options={{
          headerShown: false,
          title: 'ColourSense | Forgot Password',
        }}
      />
      <Stack.Screen
        name="AuthCallback"
        component={AuthCallbackScreen}
        options={{
          headerShown: false,
          title: 'ColourSense | Email Confirmation',
        }}
      />
      <Stack.Screen
        name="VerifyEmailRequired"
        component={VerifyEmailRequiredScreen}
        options={{
          headerShown: false,
          title: 'ColourSense | Verify Email',
        }}
      />
      <Stack.Screen
        name="AdminLogin"
        component={AdminLoginScreen}
        options={authScreenOptions}
      />
    </>
  );

  const adminScreens = () => (
    <>
      <Stack.Screen
        name="Admin"
        component={AdminScreen}
        options={{
          headerShown: false,
        }}
      />
    </>
  );

  const protectedScreens = () => (
    <>
      <Stack.Screen
        name="ColourDetail"
        component={ColourDetailScreen}
        options={defaultScreenOptions}
      />
      <Stack.Screen
        name="WearThisToday"
        component={WearThisTodayScreen}
        options={getScreenOptionsWithTitle('Outfit Suggestions')}
      />
      <Stack.Screen
        name="MoodBoard"
        component={MoodBoardScreen}
        options={
          Platform.OS === 'web'
            ? { headerShown: false }
            : getScreenOptionsWithTitle('Mood Boards')
        }
      />
      <Stack.Screen
        name="MoodBoardDetail"
        component={MoodBoardDetailScreen}
        options={getDynamicScreenOptions}
      />
      <Stack.Screen
        name="UserProfile"
        component={UserProfileWrapper}
        options={defaultScreenOptions}
      />
      {/* Analysis Screens */}
      <Stack.Screen
        name="ColourAnalysis"
        component={ColourAnalysisScreen}
        options={getScreenOptionsWithTitle('Color Analysis')}
      />
      <Stack.Screen
        name="VirtualDrapeTest"
        component={VirtualDrapeTestScreen}
        options={getScreenOptionsWithTitle('Virtual Drape Test')}
      />
      <Stack.Screen
        name="ColorGenerator"
        component={ColorGeneratorScreen}
        options={getScreenOptionsWithTitle('Color Generator')}
      />
      {/* Product Detail Screens */}
      <Stack.Screen
        name="ColorAnalysisDetail"
        component={ColorAnalysisDetailScreen}
        options={getScreenOptionsWithTitle('AI Color Analysis')}
      />
      <Stack.Screen
        name="VirtualDrapeDetail"
        component={VirtualDrapeDetailScreen}
        options={getScreenOptionsWithTitle('Virtual Drape Test')}
      />
      <Stack.Screen
        name="SmartWardrobeDetail"
        component={SmartWardrobeDetailScreen}
        options={getScreenOptionsWithTitle('Smart Wardrobe')}
      />
      <Stack.Screen
        name="PaletteGeneratorDetail"
        component={PaletteGeneratorDetailScreen}
        options={getScreenOptionsWithTitle('Colour Palette Generator')}
      />
      <Stack.Screen
        name="StyleConsultationDetail"
        component={StyleConsultationDetailScreen}
        options={getScreenOptionsWithTitle('Style Consultation')}
      />
      <Stack.Screen
        name="MakeupMatchingDetail"
        component={MakeupMatchingDetailScreen}
        options={getScreenOptionsWithTitle('Makeup Color Matching')}
      />
    </>
  );

  const authenticatedScreens = () => (
    <>
      <Stack.Screen
        name="Settings"
        component={SettingsScreen}
        options={getSettingsScreenOptions('Settings')}
      />

      <Stack.Screen
        name="ChangePassword"
        component={ChangePasswordScreen}
        options={authScreenOptions}
      />

      <Stack.Screen
        name="Billing"
        component={BillingScreen}
        options={getScreenOptionsWithTitleNoProfile('Billing & Subscription')}
      />

      <Stack.Screen
        name="Pricing"
        component={PricingScreen}
        options={getScreenOptionsWithTitleNoProfile('Choose Your Plan')}
      />

      <Stack.Screen
        name="BillingSupport"
        component={BillingSupportScreen}
        options={getScreenOptionsWithTitle('Billing Support')}
      />

      <Stack.Screen
        name="HelpCenter"
        component={HelpCenterScreen}
        options={getScreenOptionsWithTitle('Help Center')}
      />

      <Stack.Screen
        name="AccessibilitySettings"
        component={AccessibilitySettingsScreen}
        options={getScreenOptionsWithTitle('Accessibility')}
      />

      <Stack.Screen
        name="LanguageSelection"
        component={LanguageSelectionScreen}
        options={getScreenOptionsWithTitle('Language')}
      />

      <Stack.Screen
        name="ThemeSelection"
        component={ThemeSelectionScreen}
        options={getScreenOptionsWithTitle('Theme')}
      />
    </>
  );

  const premiumScreens = () => <></>;

  const hasPremiumAccess = () => {
    return (
      subscriptionPlan === SubscriptionPlan.PRO ||
      subscriptionPlan === SubscriptionPlan.FREEMIUM_TRIAL
    );
  };

  // Prioritise email verification over onboarding so newly registered users
  // are sent to VerifyEmailRequired immediately when pendingEmailVerify is set.
  const authFlowInitialRoute = needsEmailVerification()
    ? 'VerifyEmailRequired'
    : 'Onboarding';

  return (
    <Stack.Navigator
      screenOptions={{
        headerShown: false,
      }}
      initialRouteName={
        !isAuthenticated
          ? undefined
          : needsOnboarding() || needsEmailVerification()
            ? authFlowInitialRoute
            : undefined
      }
    >
      {!isAuthenticated ? (
        <Stack.Group>
          <Stack.Screen
            name="Admin"
            component={AdminScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="Login"
            component={LoginScreen}
            options={{
              ...authScreenOptions,
              title: 'ColourSense | Log in',
            }}
          />
          <Stack.Screen
            name="Landing"
            component={LandingScreen}
            options={{
              ...authScreenOptions,
              title:
                'ColourSense: Get the Perfect Shade. Real-Time, Every Time.',
            }}
          />
          <Stack.Screen
            name="PublicColorGenerator"
            component={PublicColorGeneratorScreen}
            options={{
              headerShown: false,
              title: 'ColourSense | Palette Generator',
            }}
          />
          <Stack.Screen
            name="PublicExploreColours"
            component={PublicExploreColoursScreen}
            options={{
              headerShown: false,
              title: 'ColourSense | Colour Library',
            }}
          />
          <Stack.Screen
            name="PublicColorAnalysis"
            component={PublicColorAnalysisScreen}
            options={{
              headerShown: false,
              title: 'ColourSense | Colour Analysis',
            }}
          />
          <Stack.Screen
            name="PublicVirtualDrape"
            component={PublicVirtualDrapeScreen}
            options={{
              headerShown: false,
              title: 'ColourSense | Virtual Drape',
            }}
          />
          {publicScreens()}
          <Stack.Screen
            name="Register"
            component={RegisterScreen}
            options={{
              ...authScreenOptions,
              title: 'ColourSense | Sign up',
            }}
          />
          <Stack.Screen
            name="Onboarding"
            component={OnboardingScreen}
            options={authScreenOptions}
          />
          <Stack.Screen
            name="ChangePassword"
            component={ChangePasswordScreen}
            options={authScreenOptions}
          />
        </Stack.Group>
      ) : needsOnboarding() || needsEmailVerification() ? (
        <Stack.Group>
          <Stack.Screen
            name="Onboarding"
            component={OnboardingScreen}
            options={authScreenOptions}
          />
          <Stack.Screen
            name="VerifyEmailRequired"
            component={VerifyEmailRequiredScreen}
            options={{
              headerShown: false,
              title: 'ColourSense | Verify Email',
            }}
          />
          <Stack.Screen
            name="AuthCallback"
            component={AuthCallbackScreen}
            options={{
              headerShown: false,
              title: 'ColourSense | Email Confirmation',
            }}
          />
          <Stack.Screen
            name="Login"
            component={LoginScreen}
            options={{
              ...authScreenOptions,
              title: 'ColourSense | Log in',
            }}
          />
          <Stack.Screen
            name="Register"
            component={RegisterScreen}
            options={{
              ...authScreenOptions,
              title: 'ColourSense | Sign up',
            }}
          />
          <Stack.Screen
            name="ForgotPassword"
            component={ForgotPasswordScreen}
            options={{
              headerShown: false,
              title: 'ColourSense | Forgot Password',
            }}
          />
        </Stack.Group>
      ) : (
        <Stack.Group>
          <Stack.Screen
            name="MainTabs"
            component={MainTabsWithToolbar}
            options={tabScreenOptions}
          />
          <Stack.Screen
            name="Login"
            component={LoginScreen}
            options={{
              ...authScreenOptions,
              title: 'ColourSense | Log in',
            }}
          />
          {authenticatedScreens()}
          {adminScreens()}
          {protectedScreens()}
          {publicScreens()}
          {hasPremiumAccess() && premiumScreens()}
          <Stack.Screen
            name="PremiumPaywall"
            component={PremiumPaywallScreen}
            options={defaultScreenOptions}
          />
        </Stack.Group>
      )}
    </Stack.Navigator>
  );
};

export default Navigation;
